/**
 * Cabeçalho de uma seção dentro de uma tela de módulo.
 *
 * Papel: título em `h3` serifado à esquerda e, à direita, a ação da seção.
 * O subtítulo mono curto muda de lugar conforme o que vier junto: sozinho ao
 * lado do título, ele fica à direita; com uma ação, ele desce para baixo do
 * título, no mesmo grupo dele, e a ação fica sozinha à direita.
 *
 * Como usar: quem passa `subtitulo` e `acoes` juntos — é o caso de toda
 * `SecaoCadastro` com `descricao` e botão de acrescentar — recebe o subtítulo
 * empilhado sob o título, e não ao lado do botão: não existe o desenho de
 * frase e botão lado a lado à direita, e é a presença dos dois que decide.
 * Com `subtitulo` sozinho, o subtítulo continua à direita, na mesma linha do
 * título.
 *
 * `subtitulo` quebra em mais de uma linha quando o texto passar do espaço
 * disponível, em vez de estourar a largura da tela — encurtar o texto em si é
 * decisão de quem chama, não deste componente. `titulo` fica de fora na seção
 * que não chega a ter um — é o caso do par de abas do RH doméstico —, e
 * nesse caso `acoes` sozinho ocupa o lugar que o `h3` deixa vago: como
 * `justify-between` só tem um filho para distribuir, ele fica encostado à
 * esquerda, e não à direita. Sem título não há onde empilhar, e o subtítulo
 * que vier junto fica ao lado das ações.
 *
 * No desenho empilhado, o subtítulo é texto em linha dentro do mesmo bloco do
 * título, e a linha dele herda do `body` os 15 px com entrelinha de 1,6: são
 * esses 24 px que separam o título do subtítulo, sem margem nenhuma. Dar ao
 * subtítulo um `block` com entrelinha própria mudaria essa distância.
 */
import { type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

interface CabecalhoDeSecaoProps {
  /** Título da seção; ausente na seção que não chega a ter um — só o `h3` some, não a linha inteira. */
  titulo?: string;
  /**
   * Linha mono curta; sob o título quando vier com `acoes`, ao lado dele
   * quando vier sozinha. Ausente quando a seção não tem uma.
   */
  subtitulo?: string;
  /** Conteúdo à direita do título, como o botão que abre o cadastro. */
  acoes?: ReactNode;
  className?: string;
}

/**
 * O cabeçalho de uma seção.
 *
 * @param titulo - Título da seção, à esquerda; sem ele, o `h3` não é
 *   desenhado e `acoes`/`subtitulo` ocupam a linha inteira, encostados à
 *   esquerda.
 * @param subtitulo - Linha mono da seção. Com `titulo` e `acoes` junto, fica
 *   embaixo do título, dentro do mesmo grupo; sem `acoes`, fica à direita, na
 *   linha do título.
 * @param acoes - Conteúdo à direita do título. Alinha pela linha de base do
 *   título, e não do subtítulo, quando os dois estão empilhados.
 * @param className - Classes de quem chama, aplicadas depois das internas
 *   pelo `cn()` e por isso capazes de sobrescrevê-las.
 * @returns O título, com o subtítulo embaixo quando houver ação, e à direita
 *   as ações e o subtítulo que não tiver descido.
 */
export function CabecalhoDeSecao({
  titulo,
  subtitulo,
  acoes,
  className,
}: CabecalhoDeSecaoProps) {
  const empilhado = titulo !== undefined && subtitulo !== undefined && acoes !== undefined;

  const linhaDoSubtitulo =
    subtitulo === undefined ? null : (
      <span className="font-mono text-[11px] tracking-[0.05em] text-texto-fraco">
        {subtitulo}
      </span>
    );

  const tituloDaSecao =
    titulo === undefined ? null : (
      <h3 className="font-serif text-lg font-medium text-foreground">{titulo}</h3>
    );

  if (empilhado) {
    return (
      <div
        className={cn('mb-4 flex flex-wrap items-baseline justify-between gap-2', className)}
      >
        <div>
          {tituloDaSecao}
          {linhaDoSubtitulo}
        </div>
        <div className="flex flex-wrap items-baseline gap-3">{acoes}</div>
      </div>
    );
  }

  return (
    <div className={cn('mb-4 flex flex-wrap items-baseline justify-between gap-2', className)}>
      {tituloDaSecao}
      {(subtitulo !== undefined || acoes !== undefined) && (
        <div className="flex flex-wrap items-baseline gap-3">
          {linhaDoSubtitulo}
          {acoes}
        </div>
      )}
    </div>
  );
}
